import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Copy,
  Percent,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  Ticket,
  Shield,
  ShieldOff,
  ShoppingBag,
  Package,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { api } from "../../lib/api";

const STATUS_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "active", label: "Activos" },
  { value: "used", label: "Usados" },
  { value: "expired", label: "Expirados" },
];

const QUICK_PICKS = [5, 10, 15];

const formatDT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
};

const derivedState = (p) => {
  if (p.used) return { label: "Usado", color: "bg-neutral-200 text-neutral-700" };
  if (!p.is_active) return { label: "Desactivado", color: "bg-red-100 text-red-700" };
  const now = new Date();
  const exp = new Date(p.expires_at);
  if (exp <= now) return { label: "Expirado", color: "bg-amber-100 text-amber-800" };
  return { label: "Activo", color: "bg-emerald-100 text-emerald-800" };
};

/**
 * Fase 2 · Promo codes — pantalla admin.
 * - Tabla con filtro por estado.
 * - Modal de creación con código manual + generar aleatorio + % + expira.
 * - Acciones fila: copiar, activar/desactivar, borrar (solo si no usado).
 */
export default function AdminPromoCodes() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const [openCreate, setOpenCreate] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [percent, setPercent] = useState(10);
  const [applyGrocery, setApplyGrocery] = useState(true);
  const [applyPaquete, setApplyPaquete] = useState(true);
  const [expiresAt, setExpiresAt] = useState(() => {
    // Default: mañana 23:59 local
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(23, 59, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promo-codes", { params: { status } });
      setRows(Array.isArray(data) ? data : []);
    } catch (_e) {
      toast.error("No se pudo cargar la lista de códigos");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const tomorrowISO = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  }, []);

  const openCreateModal = () => {
    setCodeInput("");
    setPercent(10);
    setApplyGrocery(true);
    setApplyPaquete(true);
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(23, 59, 0, 0);
    setExpiresAt(d.toISOString().slice(0, 16));
    setOpenCreate(true);
  };

  const generateRandom = async () => {
    try {
      const { data } = await api.post("/admin/promo-codes/generate-code");
      setCodeInput(data?.code || "");
      toast.success("Código aleatorio generado");
    } catch (_e) {
      toast.error("No se pudo generar el código");
    }
  };

  const submitCreate = async () => {
    if (percent < 1 || percent > 100) {
      toast.error("El descuento debe estar entre 1 y 100");
      return;
    }
    if (!applyGrocery && !applyPaquete) {
      toast.error("Selecciona al menos un flujo (Comida o Paquete)");
      return;
    }
    const expiresIso = new Date(expiresAt).toISOString();
    if (new Date(expiresIso) <= new Date()) {
      toast.error("La fecha de expiración debe ser futura");
      return;
    }
    setCreating(true);
    try {
      const applies_to = [];
      if (applyGrocery) applies_to.push("grocery");
      if (applyPaquete) applies_to.push("paquete");
      const body = {
        discount_percent: Number(percent),
        expires_at: expiresIso,
        applies_to,
      };
      if (codeInput.trim()) body.code = codeInput.trim().toUpperCase();
      const { data } = await api.post("/admin/promo-codes", body);
      toast.success(`Código ${data.code} creado`);
      setOpenCreate(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo crear el código";
      toast.error(typeof msg === "string" ? msg : "Error al crear");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/admin/promo-codes/${row.id}`, {
        is_active: !row.is_active,
      });
      toast.success(row.is_active ? "Código desactivado" : "Código activado");
      load();
    } catch (_e) {
      toast.error("No se pudo actualizar");
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`¿Eliminar ${row.code}?`)) return;
    try {
      await api.delete(`/admin/promo-codes/${row.id}`);
      toast.success("Código eliminado");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo eliminar";
      toast.error(typeof msg === "string" ? msg : "Error");
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`${code} copiado`);
    } catch (_e) {
      toast.error("Copia no disponible");
    }
  };

  return (
    <div data-testid="admin-promo-codes-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
            <Ticket className="h-7 w-7 text-emerald-600" />
            Códigos promocionales
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Códigos de un solo uso globales. El admin decide en qué flujo(s)
            aplica cada uno: tienda de comida{" "}
            <code className="mx-1 rounded bg-neutral-100 px-1 text-[11px]">/envio</code>
            o envío de paquete{" "}
            <code className="mx-1 rounded bg-neutral-100 px-1 text-[11px]">/enviar-paquete</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger
              className="h-10 w-[160px] rounded-full"
              data-testid="admin-promo-status-filter"
            >
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={load}
            data-testid="admin-promo-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" /> Recargar
          </Button>
          <Button
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={openCreateModal}
            data-testid="admin-promo-create-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Crear código
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-500 uppercase text-[11px] tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Código</th>
                <th className="text-left px-4 py-3">%</th>
                <th className="text-left px-4 py-3">Aplica en</th>
                <th className="text-left px-4 py-3">Expira</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="text-left px-4 py-3">Usado por</th>
                <th className="text-left px-4 py-3">Fecha uso</th>
                <th className="text-right px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-neutral-400">
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-neutral-400">
                    Aún no hay códigos con este filtro.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((p) => {
                  const st = derivedState(p);
                  return (
                    <tr
                      key={p.id}
                      data-testid={`admin-promo-row-${p.code}`}
                      className="border-t border-neutral-100 hover:bg-neutral-50"
                    >
                      <td className="px-4 py-3 font-mono font-bold">{p.code}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 font-bold">
                          <Percent className="h-3 w-3" />
                          {p.discount_percent}
                        </span>
                      </td>
                      <td
                        className="px-4 py-3"
                        data-testid={`admin-promo-row-applies-${p.code}`}
                      >
                        <div className="flex flex-wrap gap-1">
                          {(p.applies_to || []).includes("grocery") && (
                            <Badge className="border-none bg-emerald-100 text-emerald-800 gap-1">
                              <ShoppingBag className="h-3 w-3" />
                              Comida
                            </Badge>
                          )}
                          {(p.applies_to || []).includes("paquete") && (
                            <Badge className="border-none bg-amber-100 text-amber-800 gap-1">
                              <Package className="h-3 w-3" />
                              Paquete
                            </Badge>
                          )}
                          {(!p.applies_to || p.applies_to.length === 0) && (
                            <span className="text-neutral-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-neutral-600">
                        {formatDT(p.expires_at)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${st.color} border-none`}>{st.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-neutral-600">
                        {p.used_by_email || "—"}
                      </td>
                      <td className="px-4 py-3 text-neutral-600">
                        {formatDT(p.used_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2"
                            onClick={() => copyCode(p.code)}
                            data-testid={`admin-promo-copy-${p.code}`}
                            title="Copiar código"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          {!p.used && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2"
                              onClick={() => toggleActive(p)}
                              data-testid={`admin-promo-toggle-active-${p.code}`}
                              title={p.is_active ? "Desactivar" : "Activar"}
                            >
                              {p.is_active ? (
                                <ShieldOff className="h-4 w-4" />
                              ) : (
                                <Shield className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          {!p.used && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => deleteRow(p)}
                              data-testid={`admin-promo-delete-${p.code}`}
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo código promocional</DialogTitle>
            <DialogDescription>
              Los códigos son de un solo uso a nivel global. Elige a qué
              flujos aplica.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="promo-code" className="text-sm">
                Código (deja vacío para generar automático)
              </Label>
              <div className="mt-1 flex gap-2">
                <Input
                  id="promo-code"
                  data-testid="admin-promo-code-input"
                  value={codeInput}
                  onChange={(e) =>
                    setCodeInput(
                      e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase()
                    )
                  }
                  maxLength={20}
                  placeholder="Ej: RAMADAN10"
                  className="h-10 font-mono uppercase"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={generateRandom}
                  data-testid="admin-promo-generate-random-btn"
                  className="rounded-full whitespace-nowrap"
                >
                  <Sparkles className="h-4 w-4 mr-1" /> Aleatorio
                </Button>
              </div>
              <p className="mt-1 text-xs text-neutral-500">
                Solo letras A-Z y números 0-9 (4-20 caracteres). Se guardará en
                mayúsculas.
              </p>
            </div>

            <div>
              <Label className="text-sm">Descuento %</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={percent}
                  onChange={(e) => setPercent(Number(e.target.value))}
                  data-testid="admin-promo-percent-input"
                  className="h-10 w-24 text-center font-bold"
                />
                <div className="flex gap-1">
                  {QUICK_PICKS.map((q) => (
                    <Button
                      key={q}
                      type="button"
                      variant={percent === q ? "default" : "outline"}
                      onClick={() => setPercent(q)}
                      data-testid={`admin-promo-quick-${q}`}
                      className={`rounded-full h-9 ${
                        percent === q
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : ""
                      }`}
                    >
                      {q}%
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="promo-expires" className="text-sm">
                Expira el
              </Label>
              <Input
                id="promo-expires"
                type="datetime-local"
                min={tomorrowISO}
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                data-testid="admin-promo-expires-input"
                className="mt-1 h-10"
              />
            </div>

            <div>
              <Label className="text-sm">Aplica en</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label
                  htmlFor="apply-grocery"
                  className={`flex items-center gap-3 rounded-xl border-2 p-3 cursor-pointer transition-all ${
                    applyGrocery
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-neutral-200 hover:border-emerald-300"
                  }`}
                >
                  <Checkbox
                    id="apply-grocery"
                    checked={applyGrocery}
                    onCheckedChange={(v) => setApplyGrocery(!!v)}
                    data-testid="admin-promo-applies-grocery"
                  />
                  <ShoppingBag className="h-5 w-5 text-emerald-600" />
                  <div className="text-sm">
                    <div className="font-bold">Tienda comida</div>
                    <div className="text-xs text-neutral-500">/envio</div>
                  </div>
                </label>
                <label
                  htmlFor="apply-paquete"
                  className={`flex items-center gap-3 rounded-xl border-2 p-3 cursor-pointer transition-all ${
                    applyPaquete
                      ? "border-amber-500 bg-amber-50"
                      : "border-neutral-200 hover:border-amber-300"
                  }`}
                >
                  <Checkbox
                    id="apply-paquete"
                    checked={applyPaquete}
                    onCheckedChange={(v) => setApplyPaquete(!!v)}
                    data-testid="admin-promo-applies-paquete"
                  />
                  <Package className="h-5 w-5 text-amber-600" />
                  <div className="text-sm">
                    <div className="font-bold">Envío de paquete</div>
                    <div className="text-xs text-neutral-500">
                      /enviar-paquete
                    </div>
                  </div>
                </label>
              </div>
              {!applyGrocery && !applyPaquete && (
                <p className="mt-1 text-xs text-red-600">
                  Selecciona al menos un flujo.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpenCreate(false)}
              className="rounded-full"
            >
              Cancelar
            </Button>
            <Button
              onClick={submitCreate}
              disabled={creating}
              data-testid="admin-promo-submit-btn"
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {creating ? "Creando…" : "Crear código"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
